class LivingCollection extends HTMLElement {
  connectedCallback() {
    if (this.dataset.complete !== 'true' || this.controller) return;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.grid = this.querySelector('[data-grid]');
    this.cards = [...this.querySelectorAll('[data-living-card]')];
    this.cards.forEach(card => {
      try { card.forms = JSON.parse(card.dataset.forms) || []; } catch { card.forms = []; }
      if (!Array.isArray(card.forms)) card.forms = [card.forms];
    });
    this.fields = Object.fromEntries([...this.querySelectorAll('[name^="living_"]')].map(field => [field.name, field]));
    this.types = [...new Map(this.cards.map(card => [card.dataset.type, { value: card.dataset.type, label: card.dataset.typeLabel, group: card.dataset.group }])).values()];
    this.categories = [...this.querySelectorAll('[data-category]')];
    this.groups = ['all', ...new Set(this.cards.map(card => card.dataset.group))];
    this.disclosure = this.querySelector('[data-disclosure]');
    this.limit = 24;
    this.category = 'all';
    for (const selector of ['[data-categories]', '[data-tools]', '[data-pagination]']) this.querySelector(selector).hidden = false;
    this.categories.forEach(button => {
      button.querySelector('[data-category-count]').textContent = button.dataset.category === 'all' ? this.cards.length : this.cards.filter(card => card.dataset.group === button.dataset.category).length;
    });
    this.addEventListener('click', event => {
      const category = event.target.closest('[data-category]');
      if (category) {
        this.category = this.category === category.dataset.category ? 'all' : category.dataset.category;
        this.fields.living_type.value = '';
        this.fields.living_form.value = '';
        this.refreshTypes();
        this.commit();
      }
      const type = event.target.closest('[data-type-value]');
      if (type) {
        this.fields.living_type.value = type.dataset.typeValue === this.fields.living_type.value ? '' : type.dataset.typeValue;
        this.fields.living_form.value = '';
        this.commit();
      }
      if (event.target.closest('[data-reset]')) {
        this.category = 'all';
        Object.entries(this.fields).forEach(([name, field]) => {
          if (field.type === 'checkbox') field.checked = false;
          else field.value = name === 'living_sort' ? 'manual' : '';
        });
        this.refreshTypes();
        this.commit();
      }
      const remove = event.target.closest('button[data-remove]');
      if (remove) {
        const key = remove.dataset.remove;
        if (key === 'category') {
          this.category = 'all';
          this.fields.living_type.value = '';
          this.fields.living_form.value = '';
          this.refreshTypes();
        } else if (this.fields[key].type === 'checkbox') this.fields[key].checked = false;
        else this.fields[key].value = '';
        this.commit();
        (this.querySelector('[data-selection] button') || this.disclosure.querySelector('summary')).focus({preventScroll: true});
      }
      if (event.target.closest('[data-more]')) {
        const previous = this.limit;
        this.limit += 24;
        this.render();
        this.matches[previous]?.querySelector('a').focus({preventScroll: true});
      }
    }, options);
    this.addEventListener('change', event => {
      if (!event.target.name?.startsWith('living_')) return;
      this.commit();
    }, options);
    this.fields.living_q.addEventListener('input', () => {
      clearTimeout(this.searchTimer);
      this.limit = 24;
      this.render();
      this.searchTimer = setTimeout(() => this.writeUrl(true), 250);
    }, options);
    this.addEventListener('keydown', event => {
      if (event.key === 'Escape' && this.disclosure.open) {
        this.disclosure.open = false;
        this.disclosure.querySelector('summary').focus({preventScroll: true});
      }
    }, options);
    window.addEventListener('popstate', () => { clearTimeout(this.searchTimer); this.readUrl(); this.render(); }, options);
    this.header = document.querySelector('header.hdt-section-header');
    this.schedule = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        const top = Math.max(0, this.header?.getBoundingClientRect().bottom || 0);
        this.style.setProperty('--living-header', `${top}px`);
        const stuck = this.querySelector('[data-sentinel]').getBoundingClientRect().top < top;
        if (stuck && !this.wasStuck) this.disclosure.open = false;
        this.classList.toggle('is-stuck', stuck);
        this.wasStuck = stuck;
      });
    };
    window.addEventListener('scroll', this.schedule, {...options, passive: true});
    window.addEventListener('resize', this.schedule, options);
    this.header?.addEventListener('transitionend', this.schedule, options);
    this.observer = new ResizeObserver(this.schedule);
    if (this.header) this.observer.observe(this.header);
    this.readUrl();
    this.render();
    this.schedule();
  }

  refreshTypes() {
    const field = this.fields.living_type;
    const value = field.value;
    const available = this.types.filter(type => type.value && (this.category === 'all' || type.group === this.category));
    field.replaceChildren(new Option(this.dataset.allTypes, ''), ...available.map(type => new Option(type.label, type.value)));
    field.value = available.some(type => type.value === value) ? value : '';
    const quick = this.querySelector('[data-type-buttons]');
    quick.replaceChildren(...available.map(type => {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.typeValue = type.value;
      button.dataset.label = type.label;
      button.textContent = type.label;
      return button;
    }));
    quick.hidden = available.length < 2;
  }

  readUrl() {
    const params = new URL(location.href).searchParams;
    this.category = this.groups.includes(params.get('living_category')) ? params.get('living_category') : 'all';
    this.refreshTypes();
    for (const [name, field] of Object.entries(this.fields)) {
      if (field.type === 'checkbox') field.checked = params.get(name) === '1';
      else field.value = params.get(name) || (name === 'living_sort' ? 'manual' : '');
      if (field.tagName === 'SELECT' && field.selectedIndex === -1) field.selectedIndex = 0;
      if (field.type === 'number' && (!Number.isFinite(Number(field.value)) || Number(field.value) < 0)) field.value = '';
    }
    this.limit = 24;
  }

  writeUrl(replace = false) {
    const url = new URL(location.href);
    if (this.category === 'all') url.searchParams.delete('living_category');
    else url.searchParams.set('living_category', this.category);
    for (const [name, field] of Object.entries(this.fields)) {
      const value = field.type === 'checkbox' ? (field.checked ? '1' : '') : field.value.trim();
      if (value && value !== 'manual') url.searchParams.set(name, value);
      else url.searchParams.delete(name);
    }
    url.searchParams.delete('page');
    if (url.href !== location.href) history[replace ? 'replaceState' : 'pushState']({...history.state}, '', url);
  }

  commit() {
    clearTimeout(this.searchTimer);
    this.limit = 24;
    this.render();
    this.writeUrl();
  }

  activeFilters() {
    const active = [];
    if (this.category !== 'all') active.push({key: 'category', label: this.categories.find(button => button.dataset.category === this.category)?.querySelector('span:not(.gp-living__category-image)')?.textContent || this.categories.find(button => button.dataset.category === this.category)?.childNodes[0].textContent.trim()});
    for (const name of ['living_type','living_form']) {
      const field = this.fields[name];
      if (field.value) active.push({key: name, label: field.selectedOptions[0].textContent});
    }
    for (const name of ['living_min','living_max']) if (this.fields[name].value) active.push({key: name, label: `${this.fields[name].placeholder}: ${this.fields[name].value}`});
    if (this.fields.living_q.value.trim()) active.push({key: 'living_q', label: this.fields.living_q.value.trim()});
    if (this.fields.living_available.checked) active.push({key:'living_available', label:this.fields.living_available.parentElement.textContent.trim()});
    return active;
  }

  render() {
    const fields = this.fields;
    const storage = this.category === 'storage' || fields.living_type.value === 'Outdoor Storage';
    this.querySelector('[data-storage-label]').hidden = !storage;
    if (!storage) fields.living_form.value = '';
    const terms = fields.living_q.value.toLocaleLowerCase().trim().split(/\s+/).filter(Boolean);
    const min = fields.living_min.value === '' ? 0 : Math.max(0, Number(fields.living_min.value) * 100);
    const max = fields.living_max.value === '' ? Infinity : Math.max(0, Number(fields.living_max.value) * 100);
    const base = this.cards.filter(card => (this.category === 'all' || card.dataset.group === this.category) && terms.every(term => card.dataset.search.toLocaleLowerCase().includes(term)) && (!fields.living_available.checked || card.dataset.available === 'true') && Number(card.dataset.price) >= min && Number(card.dataset.price) <= max);
    const typed = base.filter(card => !fields.living_type.value || card.dataset.type === fields.living_type.value);
    this.matches = typed.filter(card => !fields.living_form.value || (fields.living_form.value === '__unknown' ? !card.forms.length : card.forms.includes(fields.living_form.value)));
    for (const button of this.querySelectorAll('[data-type-value]')) {
      const count = base.filter(card => card.dataset.type === button.dataset.typeValue).length;
      button.textContent = `${button.dataset.label} ${count}`;
      const selected = fields.living_type.value === button.dataset.typeValue;
      button.setAttribute('aria-pressed', String(selected));
      button.disabled = !count && !selected;
    }
    for (const option of fields.living_form.options) {
      if (!option.value) continue;
      option.disabled = !typed.some(card => option.value === '__unknown' ? !card.forms.length : card.forms.includes(option.value)) && option.value !== fields.living_form.value;
    }
    const sort = fields.living_sort.value;
    this.matches.sort((a,b) => sort === 'price-ascending' ? Number(a.dataset.price)-Number(b.dataset.price) : sort === 'price-descending' ? Number(b.dataset.price)-Number(a.dataset.price) : sort === 'title-ascending' ? a.dataset.search.localeCompare(b.dataset.search,document.documentElement.lang) : Number(a.dataset.index)-Number(b.dataset.index));
    this.cards.forEach(card => {card.hidden = true;});
    this.matches.slice(0,this.limit).forEach(card => {this.grid.append(card);card.hidden = false;});
    this.categories.forEach(button => button.setAttribute('aria-pressed',String(button.dataset.category === this.category)));
    this.querySelector('[data-count]').textContent = this.dataset.results.replace('__COUNT__',this.matches.length);
    this.querySelector('[data-empty]').hidden = this.matches.length !== 0;
    this.querySelector('[data-more]').hidden = this.matches.length <= this.limit;
    this.querySelector('[data-showing]').textContent = this.dataset.showing.replace('__SHOWN__',Math.min(this.limit,this.matches.length)).replace('__COUNT__',this.matches.length);
    const active = this.activeFilters();
    const selected = this.querySelector('[data-selected]');
    selected.hidden = !active.length;
    selected.textContent = this.dataset.selected.replace('__COUNT__',active.length);
    this.querySelector('[data-selection]').replaceChildren(...active.map(item => {
      const button = document.createElement('button');button.type = 'button';button.dataset.remove = item.key;
      button.textContent = `${item.label} ×`;
      button.setAttribute('aria-label',this.dataset.remove.replace('__LABEL__',item.label));return button;
    }));
  }

  disconnectedCallback() {
    this.controller?.abort();this.controller = null;
    this.observer?.disconnect();clearTimeout(this.searchTimer);cancelAnimationFrame(this.frame);
  }
}
if (!customElements.get('gp-living')) customElements.define('gp-living', LivingCollection);
